/**
 * Oeffnungszeiten Admin — Bearbeitung (v2.9.11)
 * Fuer alle eingeloggten Benutzer, keine Admin-Sonderrolle.
 * Standardzeiten, Ausnahmen, Hinweisfeld, Kontakt, Texte.
 */
import { useState, useEffect, useCallback } from 'react';
import { Clock, Save, Plus, Trash2, RefreshCw, Check, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getToken = () => {
  const saved = localStorage.getItem('gb-sylt-user');
  if (saved) { try { return JSON.parse(saved).token; } catch { return null; } }
  return null;
};
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

const DAY_NAMES = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const DAY_DEFAULTS = ['Mo-Fr Standardzeiten', 'Mo-Fr Standardzeiten', 'Mo-Fr Standardzeiten', 'Mo-Fr Standardzeiten', 'Mo-Fr Standardzeiten', 'Samstagslogik (Sommer/Winter)', 'Geschlossen'];

const TimeSlotEditor = ({ slots, onChange, label }) => (
  <div style={{ marginBottom: 12 }}>
    <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>{label}</label>
    {(slots || []).map((s, i) => (
      <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
        <input type="time" value={s.from || ''} onChange={e => { const n = [...slots]; n[i] = {...n[i], from: e.target.value}; onChange(n); }}
          className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
        <span style={{ fontSize: 13, color: '#999' }}>–</span>
        <input type="time" value={s.to || ''} onChange={e => { const n = [...slots]; n[i] = {...n[i], to: e.target.value}; onChange(n); }}
          className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
        <button onClick={() => onChange(slots.filter((_, j) => j !== i))} className="text-red-500 hover:text-red-700">
          <Trash2 style={{ width: 14, height: 14 }} />
        </button>
      </div>
    ))}
    <button onClick={() => onChange([...(slots || []), { from: '09:00', to: '17:00' }])}
      className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 mt-1">
      <Plus style={{ width: 12, height: 12 }} /> Zeitfenster
    </button>
  </div>
);

export const OeffnungszeitenAdmin = () => {
  const [config, setConfig] = useState(null);
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [sections, setSections] = useState({ times: true, notice: true, exceptions: true, contact: true, season: true, texts: false, on_call: true });
  const [newExc, setNewExc] = useState({ date: '', status: 'closed', office_times: [], phone_times: [], notice: '' });

  const toggle = (s) => setSections(p => ({ ...p, [s]: !p[s] }));

  const updateDayOverride = (dayIdx, newOverride) => {
    setConfig(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      if (!next.standard_times) next.standard_times = {};
      if (!next.standard_times.day_overrides) next.standard_times.day_overrides = {};
      if (!newOverride || newOverride.status === 'default') {
        delete next.standard_times.day_overrides[String(dayIdx)];
      } else {
        next.standard_times.day_overrides[String(dayIdx)] = newOverride;
      }
      return next;
    });
    setDirty(true);
    setSuccess(null);
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, excRes] = await Promise.all([
        axios.get(`${API}/opening-hours/config`, { headers: headers() }),
        axios.get(`${API}/opening-hours/exceptions`, { headers: headers() }),
      ]);
      setConfig(cfgRes.data.config);
      setExceptions(excRes.data.exceptions || []);
      setDirty(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveConfig = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      await axios.put(`${API}/opening-hours/config`, config, {
        headers: { ...headers(), 'Content-Type': 'application/json' }
      });
      setSuccess('Konfiguration gespeichert');
      setDirty(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally { setSaving(false); }
  };

  const updateConfig = (path, value) => {
    setConfig(prev => {
      const keys = path.split('.');
      const next = JSON.parse(JSON.stringify(prev));
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!obj[keys[i]]) obj[keys[i]] = {};
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return next;
    });
    setDirty(true); setSuccess(null);
  };

  const addException = async () => {
    if (!newExc.date) return;
    try {
      await axios.post(`${API}/opening-hours/exceptions`, newExc, {
        headers: { ...headers(), 'Content-Type': 'application/json' }
      });
      setNewExc({ date: '', status: 'closed', office_times: [], phone_times: [], notice: '' });
      loadAll();
      setSuccess('Ausnahme erstellt');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ausnahme erstellen fehlgeschlagen');
    }
  };

  const deleteException = async (id) => {
    try {
      await axios.delete(`${API}/opening-hours/exceptions/${id}`, { headers: headers() });
      loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Loeschen fehlgeschlagen');
    }
  };

  if (loading) return <div className="flex items-center justify-center p-12"><RefreshCw className="h-6 w-6 animate-spin text-gray-400" /></div>;
  if (!config) return <div className="p-6 text-center text-red-500">Konfiguration nicht verfuegbar</div>;

  const SectionHeader = ({ title, section }) => (
    <button onClick={() => toggle(section)} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
      {sections[section] ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
    </button>
  );

  const std = config.standard_times || {};

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-4" data-testid="oh-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Oeffnungszeiten verwalten</h1>
            <p className="text-sm text-gray-500">Zeiten, Ausnahmen, Kontakt — oeffentlich sichtbar</p>
          </div>
        </div>
        <button onClick={saveConfig} disabled={saving || !dirty} data-testid="oh-save-btn"
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Speichern
        </button>
      </div>

      {error && <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300"><AlertCircle className="h-4 w-4" />{error}</div>}
      {success && <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-300" data-testid="oh-success-msg"><Check className="h-4 w-4" />{success}</div>}

      {/* Globaler Hinweis */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <SectionHeader title="Globaler Hinweis" section="notice" />
        {sections.notice && (
          <div className="px-4 pb-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700 dark:text-gray-300">Hinweis aktiv</span>
              <button onClick={() => updateConfig('global_notice.active', !config.global_notice?.active)}
                className={`relative w-11 h-6 rounded-full transition-colors ${config.global_notice?.active ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                data-testid="oh-notice-toggle">
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${config.global_notice?.active ? 'translate-x-5' : ''}`} />
              </button>
            </div>
            <textarea value={config.global_notice?.text || ''} onChange={e => updateConfig('global_notice.text', e.target.value)} rows={2}
              placeholder="z.B. Telefonstoerung, eingeschraenkte Erreichbarkeit..."
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white resize-y"
              data-testid="oh-notice-text" />
          </div>
        )}
      </div>

      {/* Standardzeiten */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <SectionHeader title="Standardzeiten (Mo–Fr)" section="times" />
        {sections.times && (
          <div className="px-4 pb-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <TimeSlotEditor label="Buero" slots={std.weekday?.office} onChange={v => updateConfig('standard_times.weekday.office', v)} />
              </div>
              <div>
                <TimeSlotEditor label="Telefon" slots={std.weekday?.phone} onChange={v => updateConfig('standard_times.weekday.phone', v)} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Mittagspause</label>
              <input type="text" value={std.weekday?.lunch_break || ''} onChange={e => updateConfig('standard_times.weekday.lunch_break', e.target.value)}
                className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-48" />
            </div>
            <h4 className="text-xs font-semibold text-gray-500 mt-4 mb-2">Samstag (Sommerbetrieb)</h4>
            <TimeSlotEditor label="Buero" slots={std.saturday_summer?.office} onChange={v => updateConfig('standard_times.saturday_summer.office', v)} />
            <TimeSlotEditor label="Telefon" slots={std.saturday_summer?.phone} onChange={v => updateConfig('standard_times.saturday_summer.phone', v)} />
          </div>
        )}
      </div>

      {/* Sommerbetrieb */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <SectionHeader title="Sommerbetrieb (Samstag)" section="season" />
        {sections.season && (
          <div className="px-4 pb-4 flex items-center gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">KW Start</label>
              <input type="number" min={1} max={52} value={config.summer_season?.kw_start || 14}
                onChange={e => updateConfig('summer_season.kw_start', parseInt(e.target.value) || 14)}
                className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                data-testid="oh-kw-start" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">KW Ende</label>
              <input type="number" min={1} max={52} value={config.summer_season?.kw_end || 41}
                onChange={e => updateConfig('summer_season.kw_end', parseInt(e.target.value) || 41)}
                className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                data-testid="oh-kw-end" />
            </div>
            <p className="text-xs text-gray-400 mt-4">Samstag ist innerhalb dieser Wochen geoeffnet</p>
          </div>
        )}
      </div>

      {/* Rufbereitschaft */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <SectionHeader title="Rufbereitschaft" section="on_call" />
        {sections.on_call && (
          <div className="px-4 pb-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700 dark:text-gray-300">Rufbereitschaft aktiv</span>
              <button onClick={() => updateConfig('on_call.active', !(config.on_call?.active ?? true))}
                className={`relative w-11 h-6 rounded-full transition-colors ${(config.on_call?.active ?? true) ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                data-testid="oh-oncall-toggle">
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${(config.on_call?.active ?? true) ? 'translate-x-5' : ''}`} />
              </button>
            </div>
            <div className="flex items-center gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Start (geschlossene Tage)</label>
                <input type="time" value={config.on_call?.start_closed || '10:00'}
                  onChange={e => updateConfig('on_call.start_closed', e.target.value)}
                  className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  data-testid="oh-oncall-start" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Ende</label>
                <input type="time" value={config.on_call?.end || '21:30'}
                  onChange={e => updateConfig('on_call.end', e.target.value)}
                  className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  data-testid="oh-oncall-end" />
              </div>
            </div>
            <p className="text-xs text-gray-400">
              An geoeffneten Tagen beginnt die Rufbereitschaft automatisch nach der letzten Buerozeit.
              An geschlossenen Tagen ab der konfigurierten Startzeit.
            </p>
          </div>
        )}
      </div>

      {/* Wochentage Einzelsteuerung */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <SectionHeader title="Wochentage (Einzelsteuerung)" section="days" />
        {sections.days && (
          <div className="px-4 pb-4 space-y-2">
            <p className="text-xs text-gray-500 mb-2">
              Einzelne Wochentage gezielt uebersteuern. &quot;Standard&quot; nutzt die oben definierten Standardzeiten.
            </p>
            {DAY_NAMES.map((name, i) => {
              const override = std.day_overrides?.[String(i)];
              const status = override?.status || 'default';
              return (
                <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3" data-testid={`day-override-${i}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{name}</span>
                      {status === 'default' && (
                        <span className="ml-2 text-xs text-gray-400">({DAY_DEFAULTS[i]})</span>
                      )}
                    </div>
                    <select
                      value={status}
                      onChange={e => {
                        const v = e.target.value;
                        if (v === 'default') updateDayOverride(i, null);
                        else if (v === 'closed') updateDayOverride(i, { status: 'closed', notice: override?.notice || '' });
                        else {
                          const defaults = i < 5 ? std.weekday : (i === 5 ? std.saturday_summer : std.sunday);
                          updateDayOverride(i, {
                            status: 'open',
                            office: override?.office || defaults?.office || [],
                            phone: override?.phone || defaults?.phone || [],
                          });
                        }
                      }}
                      className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      data-testid={`day-status-${i}`}
                    >
                      <option value="default">Standard</option>
                      <option value="open">Geoeffnet</option>
                      <option value="closed">Geschlossen</option>
                    </select>
                  </div>
                  {status === 'open' && (
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <TimeSlotEditor label="Buero" slots={override?.office || []} onChange={v => updateDayOverride(i, { ...override, office: v })} />
                      <TimeSlotEditor label="Telefon" slots={override?.phone || []} onChange={v => updateDayOverride(i, { ...override, phone: v })} />
                    </div>
                  )}
                  {status === 'closed' && (
                    <input type="text" value={override?.notice || ''}
                      onChange={e => updateDayOverride(i, { ...override, notice: e.target.value })}
                      placeholder="Hinweis (optional, z.B. Ruhetag)"
                      className="w-full mt-2 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      data-testid={`day-notice-${i}`} />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Ausnahmen */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <SectionHeader title="Ausnahmen / Feiertage" section="exceptions" />
        {sections.exceptions && (
          <div className="px-4 pb-4 space-y-3">
            {/* Neue Ausnahme */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-gray-600 dark:text-gray-400">Neue Ausnahme</p>
              <div className="grid grid-cols-3 gap-2">
                <input type="date" value={newExc.date} onChange={e => setNewExc(p => ({...p, date: e.target.value}))}
                  className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  data-testid="exc-date" />
                <select value={newExc.status} onChange={e => setNewExc(p => ({...p, status: e.target.value}))}
                  className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  data-testid="exc-status">
                  <option value="closed">Geschlossen</option>
                  <option value="modified">Abweichende Zeiten</option>
                  <option value="open">Sonderoeffnung</option>
                </select>
                <input type="text" value={newExc.notice} onChange={e => setNewExc(p => ({...p, notice: e.target.value}))}
                  placeholder="Hinweis (optional)" data-testid="exc-notice"
                  className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>
              {newExc.status !== 'closed' && (
                <div className="grid grid-cols-2 gap-2">
                  <TimeSlotEditor label="Buero" slots={newExc.office_times} onChange={v => setNewExc(p => ({...p, office_times: v}))} />
                  <TimeSlotEditor label="Telefon" slots={newExc.phone_times} onChange={v => setNewExc(p => ({...p, phone_times: v}))} />
                </div>
              )}
              <button onClick={addException} disabled={!newExc.date} data-testid="exc-add-btn"
                className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 disabled:opacity-50">
                <Plus className="h-3 w-3" /> Ausnahme anlegen
              </button>
            </div>

            {/* Bestehende Ausnahmen */}
            {exceptions.length === 0 && <p className="text-sm text-gray-400 text-center py-2">Keine Ausnahmen eingetragen</p>}
            {exceptions.map(exc => (
              <div key={exc.id} className="flex items-center justify-between p-2.5 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{exc.date}</span>
                  <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                    exc.status === 'closed' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                    exc.status === 'modified' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                  }`}>
                    {exc.status === 'closed' ? 'Geschlossen' : exc.status === 'modified' ? 'Abweichend' : 'Sonderoeffnung'}
                  </span>
                  {exc.notice && <span className="ml-2 text-xs text-gray-500">{exc.notice}</span>}
                </div>
                <button onClick={() => deleteException(exc.id)} className="text-red-400 hover:text-red-600" data-testid={`exc-del-${exc.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Kontaktbereich */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <SectionHeader title="Kontaktbereich" section="contact" />
        {sections.contact && (
          <div className="px-4 pb-4 space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">E-Mail-Adresse</label>
              <input type="email" value={config.contact?.email || ''} onChange={e => updateConfig('contact.email', e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                data-testid="oh-contact-email" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Link zum Kontaktformular</label>
              <input type="url" value={config.contact?.form_link || ''} onChange={e => updateConfig('contact.form_link', e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                data-testid="oh-contact-link" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Hinweistext</label>
              <textarea value={config.contact?.hint_text || ''} onChange={e => updateConfig('contact.hint_text', e.target.value)} rows={2}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white resize-y"
                data-testid="oh-contact-hint" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Text bei geschlossen</label>
              <textarea value={config.contact?.closed_text || ''} onChange={e => updateConfig('contact.closed_text', e.target.value)} rows={2}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white resize-y"
                data-testid="oh-contact-closed" />
            </div>
          </div>
        )}
      </div>

      {/* Texte */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <SectionHeader title="Texte anpassen" section="texts" />
        {sections.texts && (
          <div className="px-4 pb-4 grid grid-cols-2 gap-3">
            {Object.entries(config.texts || {}).map(([key, val]) => (
              <div key={key}>
                <label className="block text-xs font-semibold text-gray-500 mb-1">{key}</label>
                <input type="text" value={val || ''} onChange={e => updateConfig(`texts.${key}`, e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  data-testid={`oh-text-${key}`} />
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-gray-400 text-center">Aenderungen an Ausnahmen werden sofort wirksam. Andere Aenderungen nach Klick auf Speichern.</p>
    </div>
  );
};
